// OAuth Configuration
const _origin = typeof window !== 'undefined' ? window.location.origin : 'https://joylo.ai';
const OAUTH_CONFIG = {
  google: {
    clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID,
    redirectUri: `${_origin}/auth/callback/google`,
    scope: 'openid email profile',
    responseType: 'code',
    authUrl: 'https://accounts.google.com/o/oauth2/v2/auth'
  },
  github: {
    clientId: import.meta.env.VITE_GITHUB_CLIENT_ID,
    redirectUri: `${_origin}/auth/callback/github`,
    scope: 'user:email',
    authUrl: 'https://github.com/login/oauth/authorize'
  },
  facebook: {
    clientId: import.meta.env.VITE_FACEBOOK_CLIENT_ID,
    redirectUri: `${_origin}/auth/callback/facebook`,
    scope: 'email',
    responseType: 'code',
    authUrl: 'https://www.facebook.com/v18.0/dialog/oauth'
  },
  apple: {
    clientId: import.meta.env.VITE_APPLE_CLIENT_ID,
    redirectUri: `${_origin}/auth/callback/apple`,
    scope: 'name email',
    responseType: 'code id_token',
    responseMode: 'form_post',
    authUrl: 'https://appleid.apple.com/auth/authorize'
  },
  microsoft: {
    clientId: import.meta.env.VITE_MICROSOFT_CLIENT_ID,
    redirectUri: `${_origin}/auth/callback/microsoft`,
    scope: 'openid email profile User.Read',
    responseType: 'code',
    authUrl: 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize'
  }
};

// Generate secure random state parameter
const generateState = (): string => {
  const array = new Uint32Array(4);
  crypto.getRandomValues(array);
  return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
};

const POST_AUTH_REDIRECT_KEY = 'post_auth_redirect_v2';
const POST_AUTH_REDIRECT_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Save the current page so the user can be sent back after login/OAuth.
// Skips auth-related paths that would create redirect loops.
export const savePostAuthRedirect = (path?: string): void => {
  if (typeof window === 'undefined') return;
  const p = path ?? (window.location.pathname + window.location.search);
  if (!p || p === '/' || /^\/(login|signup|auth)/.test(p)) return;
  sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, JSON.stringify({ path: p, ts: Date.now() }));
};

// Read and clear the saved redirect. Returns null if missing or expired.
export const consumePostAuthRedirect = (): string | null => {
  try {
    const raw = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
    sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
    if (!raw) return null;
    const { path, ts } = JSON.parse(raw) as { path: string; ts: number };
    if (Date.now() - ts > POST_AUTH_REDIRECT_TTL_MS) return null;
    return path || null;
  } catch {
    return null;
  }
};

// Keys used by the legacy / query-param (`?rd=`) redirect flow. These are
// written by OAuthButtons and the Login/Signup pages, and must be honoured by
// the OAuth callback so SSO logins land on the same page a manual login would.
const STORED_REDIRECT_KEYS = ['oauth_redirect_after', 'post_auth_redirect'] as const;

// Validate a post-auth redirect target. Allows relative in-app paths and
// absolute https URLs on trusted Joylo domains (e.g. preview share links like
// https://<project>-app.joylo.org/). Mirrors the check used on the Login page.
export const isValidRedirectUrl = (url: string | null | undefined): boolean => {
  try {
    if (!url) return false;
    // Browsers treat "//host" and "/\host" as another origin, not an in-app path (security scan F169).
    if (url.startsWith('/')) return !url.startsWith('//') && !url.startsWith('/\\');

    const urlObj = new URL(url);
    if (urlObj.protocol !== 'https:') return false;

    const allowedDomains = ['joylo.org', 'joylo.io', 'joylo.dev', 'joylo.ai'];
    return allowedDomains.some(
      domain => urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
    );
  } catch {
    return false;
  }
};

// Read (and clear) a redirect target saved in localStorage by the query-param
// (`?rd=`) flow. Returns the first valid candidate, or null.
export const consumeStoredRedirect = (): string | null => {
  let found: string | null = null;
  for (const key of STORED_REDIRECT_KEYS) {
    const value = localStorage.getItem(key);
    if (!found && value && isValidRedirectUrl(value)) {
      found = value;
    }
    localStorage.removeItem(key);
  }
  return found;
};

// Post-auth target for a login/signup URL: an explicit ?rd= wins, else joylo-web's paid-plan CTAs send ?plan= straight to that plan's checkout
export const postAuthRedirectFromSearch = (search: string): string | null => {
  const params = new URLSearchParams(search);
  const plan = params.get('plan')?.trim();
  return params.get('rd') || (plan ? `/billing/payment?${new URLSearchParams({ plan })}` : null);
};

// Peer-invite/referral code captured from a shared signup link (`?ref=CODE`).
// Stored in localStorage (like the `rd`/post-auth-redirect keys above) so it
// survives the full-page OAuth provider redirect round-trip.
const INVITE_CODE_KEY = 'invite_code';

export const captureInviteCodeFromUrl = (): void => {
  if (typeof window === 'undefined') return;
  const ref = new URLSearchParams(window.location.search).get('ref');
  if (ref) {
    localStorage.setItem(INVITE_CODE_KEY, ref);
  }
};

// Peek without clearing — a failed signup attempt shouldn't lose the code before retry.
export const getStoredInviteCode = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(INVITE_CODE_KEY);
};

// Lets a manual "have a code?" field on the signup form stage a value into the
// same slot the OAuth flow reads from — the code just needs to be here before
// the user clicks a provider button, no separate OAuth plumbing required.
export const setStoredInviteCode = (code: string): void => {
  if (typeof window === 'undefined') return;
  const trimmed = code.trim();
  if (trimmed) {
    localStorage.setItem(INVITE_CODE_KEY, trimmed);
  } else {
    localStorage.removeItem(INVITE_CODE_KEY);
  }
};

export const clearInviteCode = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(INVITE_CODE_KEY);
};

const setOAuthState = (state: string, provider: string) => {
  sessionStorage.setItem('oauth_state', state);
  sessionStorage.setItem('oauth_provider', provider);
  savePostAuthRedirect();
};

export const getOAuthState = (): { state: string | null, provider: string | null } => {
  const state = sessionStorage.getItem('oauth_state');
  const provider = sessionStorage.getItem('oauth_provider');
  return { state, provider };
};

// Build OAuth URL with parameters
const buildOAuthUrl = (baseUrl: string, params: Record<string, string>): string => {
  const url = new URL(baseUrl);
  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });
  return url.toString();
};

// Google OAuth
export const initiateGoogleAuth = (): string => {
  const state = generateState();
  setOAuthState(state, 'google');

  const params = {
    client_id: OAUTH_CONFIG.google.clientId,
    redirect_uri: OAUTH_CONFIG.google.redirectUri,
    scope: OAUTH_CONFIG.google.scope,
    response_type: OAUTH_CONFIG.google.responseType,
    state,
    access_type: 'offline',
    //prompt: 'consent'
  };

  return buildOAuthUrl(OAUTH_CONFIG.google.authUrl, params);
};

// GitHub OAuth
export const initiateGitHubAuth = (): string => {
  const state = generateState();
  setOAuthState(state, 'github');

  const params = {
    client_id: OAUTH_CONFIG.github.clientId,
    redirect_uri: OAUTH_CONFIG.github.redirectUri,
    scope: OAUTH_CONFIG.github.scope,
    state,
    allow_signup: 'true'
  };

  return buildOAuthUrl(OAUTH_CONFIG.github.authUrl, params);
};

// Facebook OAuth
export const initiateFacebookAuth = (): string => {
  const state = generateState();
  setOAuthState(state, 'facebook');

  const params = {
    client_id: OAUTH_CONFIG.facebook.clientId,
    redirect_uri: OAUTH_CONFIG.facebook.redirectUri,
    scope: OAUTH_CONFIG.facebook.scope,
    response_type: OAUTH_CONFIG.facebook.responseType,
    state
  };

  return buildOAuthUrl(OAUTH_CONFIG.facebook.authUrl, params);
};

// Apple OAuth
export const initiateAppleAuth = (): string => {
  const state = generateState();
  setOAuthState(state, 'apple');

  const params = {
    client_id: OAUTH_CONFIG.apple.clientId,
    redirect_uri: OAUTH_CONFIG.apple.redirectUri,
    scope: OAUTH_CONFIG.apple.scope,
    response_type: OAUTH_CONFIG.apple.responseType,
    response_mode: OAUTH_CONFIG.apple.responseMode,
    state
  };

  return buildOAuthUrl(OAUTH_CONFIG.apple.authUrl, params);
};

// Microsoft OAuth
export const initiateMicrosoftAuth = (): string => {
  const state = generateState();
  setOAuthState(state, 'microsoft');

  const params = {
    client_id: OAUTH_CONFIG.microsoft.clientId,
    redirect_uri: OAUTH_CONFIG.microsoft.redirectUri,
    scope: OAUTH_CONFIG.microsoft.scope,
    response_type: OAUTH_CONFIG.microsoft.responseType,
    state
  };

  return buildOAuthUrl(OAUTH_CONFIG.microsoft.authUrl, params);
};

// Handle OAuth callback
export const handleOAuthCallback = async (
  provider: string,
  code: string,
  state: string
): Promise<{ user: any; token: string }> => {
  // Verify state parameter
  const storedState = sessionStorage.getItem('oauth_state');
  if (state !== storedState) {
    throw new Error('Invalid state parameter');
  }

  // Clean up stored state
  sessionStorage.removeItem('oauth_state');
  sessionStorage.removeItem('oauth_provider');

  // Send code to backend for token exchange
  const response = await fetch(`${import.meta.env.VITE_BASE_API_URL}/auth/oauth/callback`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: "include",
    body: JSON.stringify({
      provider,
      code,
      redirect_uri: OAUTH_CONFIG[provider as keyof typeof OAUTH_CONFIG].redirectUri
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'OAuth authentication failed');
  }

  return await response.json();
};

// Parse URL parameters (for callback handling)
export const parseUrlParams = (url: string = window.location.href): Record<string, string> => {
  const urlObj = new URL(url);
  const params: Record<string, string> = {};

  urlObj.searchParams.forEach((value, key) => {
    params[key] = value;
  });

  return params;
};

// Validate OAuth configuration — only checks the specified provider (or active ones by default)
export const validateOAuthConfig = (provider?: string): { valid: boolean; missing: string[] } => {
  const missing: string[] = [];

  const toCheck = provider ? [provider] : ['google', 'github', 'microsoft'];

  if (toCheck.includes('google') && !OAUTH_CONFIG.google.clientId) missing.push('VITE_GOOGLE_CLIENT_ID');
  if (toCheck.includes('github') && !OAUTH_CONFIG.github.clientId) missing.push('VITE_GITHUB_CLIENT_ID');
  if (toCheck.includes('microsoft') && !OAUTH_CONFIG.microsoft.clientId) missing.push('VITE_MICROSOFT_CLIENT_ID');

  return {
    valid: missing.length === 0,
    missing
  };
};